import React from 'react';
import { Suggestion } from '../../types/suggestions';
import { SchemaField, SchemaFieldConfig } from '../../types/careerCard';
import { useCareerCard } from '../../hooks/useCareerCard';

interface SuggestionPanelProps {
  suggestions: Suggestion[];
  onAccept: (suggestion: Suggestion) => void;
  onReject: (suggestion: Suggestion) => void;
  onUndo: (field: string) => void;
  acceptedFields: Set<string>;
  cardType: string;
}

const renderNestedValue = (value: any[], config: SchemaField) => {
  console.log('Rendering nested value:', {
    value,
    config,
    fields: config.fields
  });

  if (!Array.isArray(value) || !config.fields) {
    console.log('Value is not an array or no fields config:', value);
    return String(value || '');
  }

  // Sort fields by order
  const sortedFields = config.fields
    .filter(field => typeof field === 'object')
    .sort((a, b) => {
      const orderA = typeof a === 'object' ? (a.order || 0) : 0;
      const orderB = typeof b === 'object' ? (b.order || 0) : 0;
      return orderA - orderB;
    });

  console.log('Sorted fields:', sortedFields);

  return (
    <div className="space-y-4">
      {value.map((item, index) => {
        console.log(`Rendering item ${index}:`, item);
        
        // Skip if item is not an object
        if (typeof item !== 'object' || !item) return null;

        return (
          <div key={index} className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
            <div className="font-medium mb-2 text-gray-300">Task {index + 1}:</div>
            {sortedFields.map(field => {
              if (typeof field !== 'object') return null;
              
              const fieldValue = item[field.name];
              console.log(`Field ${field.name}:`, {
                config: field,
                value: fieldValue
              });

              if (fieldValue === undefined || fieldValue === null) return null;
              
              return (
                <div key={field.name} className="mb-2 pl-2">
                  <span className="font-medium text-gray-400">{field.label}: </span>
                  <span className="text-gray-300 whitespace-pre-wrap">{String(fieldValue)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const renderValue = (value: unknown, field: string, schema: Record<string, SchemaField> | undefined): React.ReactNode => {
  if (!schema || !schema[field]) {
    return String(value || '');
  }

  const config = schema[field];

  switch (config.type) {
    case 'nested':
      if (!Array.isArray(value)) {
        if (typeof value === 'object' && value !== null) {
          // Handle single object case
          return renderNestedValue([value], config);
        }
        return String(value || '');
      }
      return renderNestedValue(value, config);

    case 'array':
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((item, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-800 dark:text-gray-200"
              >
                {typeof item === 'object' ? JSON.stringify(item) : String(item)}
              </span>
            ))}
          </div>
        );
      }
      return String(value || '');

    case 'date':
      return value ? String(value) : 'No date set';

    default:
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2);
      }
      return String(value || '');
  }
};

export const SuggestionPanel: React.FC<SuggestionPanelProps> = ({
  suggestions,
  onAccept,
  onReject,
  onUndo,
  acceptedFields,
  cardType,
}) => {
  const { schemas } = useCareerCard();
  
  const toTitleCase = (str: string): string => {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="space-y-4">
      {suggestions.map((suggestion, index) => {
        const isAccepted = acceptedFields.has(suggestion.field);
        const schema = schemas.find(s => s.card_type === cardType)?.schema;

        console.log('Rendering suggestion:', {
          field: suggestion.field,
          currentValue: suggestion.currentValue,
          suggestedValue: suggestion.suggestedValue,
          schema: schema?.[suggestion.field],
          cardType
        });

        return (
          <div
            key={`${suggestion.field}-${index}`}
            className="p-4 rounded-lg bg-gray-800 border border-gray-700"
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-semibold text-white">{toTitleCase(suggestion.field)}</h3>
              <div className="flex gap-2">
                {!isAccepted ? (
                  <>
                    <button
                      onClick={() => onAccept(suggestion)}
                      className="px-4 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => onReject(suggestion)}
                      className="px-4 py-1 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                      Reject
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => onUndo(suggestion.field)}
                    className="px-4 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                  >
                    Undo
                  </button>
                )}
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-2">{suggestion.explanation || 'Based on existing content'}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 mb-1">Current Value:</p>
                <div className="p-2 bg-gray-900 rounded text-gray-300 whitespace-pre-line">
                  {renderValue(suggestion.currentValue, suggestion.field, schema)}
                </div>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Suggested Value:</p>
                <div className="p-2 bg-gray-900 rounded text-gray-300 whitespace-pre-line">
                  {renderValue(suggestion.suggestedValue, suggestion.field, schema)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}; 